import type {MouseEvent, ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {Text, TextSize, TextType} from '@Components/text';
import {initUpload, removeUploadHandlers} from '@Libraries/upload-library';
import type {UserMediaType, UserMediaVOResponse} from '@Libraries/user-media-library';
import {getReadableMaxAllowedUploadFileSize} from '@Libraries/user-media-library';
import {getMediaStorageForbackendData, MultiMediaUploadSource, uploadAudios, uploadBackground, uploadFiles, uploadImages, uploadVideos} from '@Libraries/user-media-library';
import {
  COLLECTION_MEDIA_GRID_ID,
  UploadsMediaSubTab,
  UploadsMediaType,
  USER_UPLOADS_MEDIA_SUB_TABS,
  USER_UPLOADS_MEDIA_TABS,
  UserMediaPanelAiOption,
} from '@Panels/user-media-panel/user-media-panel.types';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {useSelectedTab} from '@Components/tabs';
import {noop} from '@Utils/general.util';
import {ClickableDiv} from '@Components/clickable-div';
import {isImageFileValid, isVectorFileValid} from '@Libraries/user-image-library';
import {isVideoFileValid} from '@Libraries/user-video-library';
import {isAudioFileValid} from '@Libraries/user-audio-library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import type {RECORD_DROPDOWN_OPTIONS} from '@Panels/user-media-panel/components/record-dropdown';
import {RecordDropdown} from '@Panels/user-media-panel/components/record-dropdown';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import type {ElementData, TempImageData} from '@Libraries/add-media-library';
import {isTempImageData} from '@Libraries/add-media-library';
import {isUserLoggedIn} from '@Libraries/user.library';
import {addItemsToGrid, addOrReplaceGridItems} from '@Components/base-grid';
import {getGridIdForTabStates} from '@Panels/user-media-panel/user-media.library';
import {updateUnlinkedMediaState} from '@Panels/user-media-panel/user-media-panel-reducer';
import styles from './upload-button-section.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import AiOptionsDropdown from '@Panels/user-media-panel/components/ai-options-dropdown';

interface UploadButtonSectionProps {
  isSmallScreen?: boolean;
  uploadAcceptedMimetype?: string;
  btnClasses?: string;
  uploadVectors: boolean;
  addInBulk?: boolean;
  shouldLoadDataUrlImage?: boolean;
  defaultTab: UploadsMediaType;
  mediaTypesToShow: UploadsMediaType[];
  panelId?: string;
  hideRecordingOptions?: boolean;
  uploadSource?: MultiMediaUploadSource;
  maxFilesToUpload?: number;

  onClose(): void;
  onMediaUploaded(items: (GridItemStorage | TempImageData)[], currentUploadIndex?: number, totalUploadsCount?: number): void;
  onClickRecordOptions(id: RECORD_DROPDOWN_OPTIONS): void;
  addSelectedItems(items?: ElementData[]): void;
  onClickAiImage(e: MouseEvent): void;
  onClickAiVoice(e: MouseEvent): void;
  onStartAddSelected?: () => void;
  onUploadError?: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void;
}

export function UploadButtonSection({
  uploadSource = MultiMediaUploadSource.UPLOAD,
  isSmallScreen = false,
  uploadAcceptedMimetype = '',
  btnClasses = '',
  onMediaUploaded = noop,
  onClose = noop,
  addInBulk = false,
  shouldLoadDataUrlImage = false,
  defaultTab = UploadsMediaType.IMAGE,
  mediaTypesToShow = [],
  uploadVectors = false,
  hideRecordingOptions = false,
  onStartAddSelected = (): void => {},
  onUploadError = (): void => {},
  ...props
}: UploadButtonSectionProps): ReactElement {
  const isForDesignUpload = useAppSelector((state) => {
    return state.mediaUploads.isForDesignUpload;
  });
  const activeTab = useSelectedTab(USER_UPLOADS_MEDIA_TABS, defaultTab) as UploadsMediaType;
  const activeSubtab = useSelectedTab(USER_UPLOADS_MEDIA_SUB_TABS, UploadsMediaSubTab.UPLOADS) as UploadsMediaSubTab;
  const currentCollectionId = useAppSelector((state) => {
    return state.mediaUploads.collection;
  });
  const fileUploadInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const onUploadItem = (): void => {
    if (fileUploadInputRef.current) {
      fileUploadInputRef.current.click();
    }
  };
  useEffect(() => {
    initMediaUpload();
    return () => {
      removeMediaUploadHandlers();
    };
  }, [activeTab, currentCollectionId]);
  const initMediaUpload = (): void => {
    if (fileUploadInputRef.current) {
      initUpload($(fileUploadInputRef.current), processFiles, () => {}, `.upload-btn-container`, window.i18next.t('pmwjs_drag_drop_message'));
    }
  };
  const removeMediaUploadHandlers = (): void => {
    if (fileUploadInputRef.current) {
      removeUploadHandlers($(fileUploadInputRef.current), `.upload-btn-container`);
    }
  };
  const filterUnAllowedFiles = async (files: File[]): Promise<File[]> => {
    const filesToUpload: File[] = [];

    const validationPromises = files.map(async (file) => {
      try {
        if (mediaTypesToShow.includes(UploadsMediaType.IMAGE) && isImageFileValid(file) && !(isVectorFileValid(file) && !uploadVectors)) {
          filesToUpload.push(file);
        } else if (mediaTypesToShow.includes(UploadsMediaType.VIDEO) && (await isVideoFileValid(file))) {
          filesToUpload.push(file);
        } else if (mediaTypesToShow.includes(UploadsMediaType.AUDIO) && isAudioFileValid(file)) {
          filesToUpload.push(file);
        }
      } catch (e) {
        console.error(e);
      }
    });

    await Promise.all(validationPromises);
    return filesToUpload;
  };

  const getInvalidFileError = (invalidFilesCount: number): string => {
    if (invalidFilesCount > 1) {
      if (mediaTypesToShow.includes(UploadsMediaType.AUDIO)) {
        return window.i18next.t('pmwjs_upload_some_invalid_audio_files', {numfiles: invalidFilesCount, uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize()});
      }
      if (activeTab === UploadsMediaType.IMAGE_BACKGROUND) {
        return window.i18next.t('pmwjs_upload_all_invalid_image_files', {numfiles: invalidFilesCount, uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize()});
      }
      return window.i18next.t('pmwjs_upload_some_invalid_image_video_files', {numfiles: invalidFilesCount, uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize()});
    }
    if (mediaTypesToShow.includes(UploadsMediaType.AUDIO)) {
      return window.i18next.t('pmwjs_upload_some_invalid_audio_file', {
        uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize(),
      });
    }
    if (activeTab === UploadsMediaType.IMAGE_BACKGROUND) {
      return window.i18next.t('pmwjs_upload_invalid_image_file', {
        uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize(),
      });
    }
    return window.i18next.t('pmwjs_upload_some_invalid_image_video_file', {
      uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize(),
    });
  };
  const isInImageTabInEmailMaker = (): boolean => {
    return mediaTypesToShow.length === 1 && mediaTypesToShow[0] === UploadsMediaType.IMAGE && uploadSource === MultiMediaUploadSource.EMAIL_UPLOAD;
  };
  const processFiles = async (files: File[]): Promise<void> => {
    onStartAddSelected();
    if (props.maxFilesToUpload && props.maxFilesToUpload < files.length) {
      files.splice(props.maxFilesToUpload, files.length);
      showMessageGrowl({text: window.i18next.t('pmwjs_max_allowed_upload_files_warning', {numfiles: props.maxFilesToUpload}), type: GROWL_TYPE.NEUTRAL});
    }

    let filesToUpload = files;
    if (mediaTypesToShow.length < 3) {
      filesToUpload = await filterUnAllowedFiles(files);
    }

    if (files.length !== filesToUpload.length) {
      showMessageGrowl({text: getInvalidFileError(files.length - filesToUpload.length), type: GROWL_TYPE.DANGER});
    }
    if (filesToUpload.length) {
      onClose();
    } else {
      return;
    }
    if (activeTab === UploadsMediaType.IMAGE_BACKGROUND) {
      void uploadBackground(
        filesToUpload.slice(0, 1),
        {onFileUpload: onFileUploaded, onUploadError, onAfterUpload: processAfterUpload},
        {collectionId: currentCollectionId, addInBulk, shouldLoadDataUrlImage}
      );
      return;
    }
    if (!currentCollectionId) {
      void uploadFiles({onFileUpload: onFileUploaded, onUploadError, onAfterUpload: processAfterUpload}, filesToUpload, {
        collectionId: currentCollectionId,
        uploadSrc: uploadSource,
        addInBulk,
        isUploadedFromImageTabInEmailMaker: isInImageTabInEmailMaker(),
        shouldLoadDataUrlImage,
      });
      return;
    }
    switch (activeTab) {
      case UploadsMediaType.IMAGE:
        void uploadImages(
          filesToUpload,
          {onFileUpload: onFileUploaded, onUploadError, onAfterUpload: processAfterUpload},
          {
            collectionId: currentCollectionId,
            uploadSrc: uploadSource,
            uploadVectors,
            addInBulk,
            isUploadedFromImageTabInEmailMaker: isInImageTabInEmailMaker(),
            shouldLoadDataUrlImage,
          }
        );
        break;
      case UploadsMediaType.VIDEO:
        void uploadVideos(filesToUpload, onFileUploaded, onUploadError, {collectionId: currentCollectionId, uploadSrc: uploadSource, addInBulk});
        break;
      case UploadsMediaType.AUDIO:
        void uploadAudios(filesToUpload, onFileUploaded, onUploadError, {collectionId: currentCollectionId, uploadSrc: uploadSource});
        break;
      default:
        throw new Error(`Invalid media type ${activeTab}`);
    }
  };
  const onFileUploaded = (
    type: UserMediaType,
    data: (UserMediaVOResponse | TempImageData)[] | UserMediaVOResponse | TempImageData,
    currentUploadIndex?: number,
    totalUploadsCount?: number
  ): void => {
    const mediaItems = [];
    const gridMediaItems = [];
    const gridItems: Record<string, GridItemStorage> = {};
    if (Array.isArray(data)) {
      for (const itemData of data) {
        if (isTempImageData(itemData)) {
          mediaItems.push(itemData);
        } else {
          gridItems[itemData.filename] = getMediaStorageForbackendData(itemData, type);
          mediaItems.push(getMediaStorageForbackendData(itemData, type));
          gridMediaItems.push(getMediaStorageForbackendData(itemData, type));
        }
      }
    } else if (isTempImageData(data)) {
      mediaItems.push(data);
    } else {
      gridItems[data.filename] = getMediaStorageForbackendData(data, type);
      mediaItems.push(getMediaStorageForbackendData(data, type));
      gridMediaItems.push(getMediaStorageForbackendData(data, type));
    }
    if (Object.keys(gridItems).length > 0) {
      if (isUserLoggedIn() && !currentCollectionId) {
        dispatch(
          addOrReplaceGridItems({
            gridId: getGridIdForTabStates(activeTab, activeSubtab),
            gridItems,
          })
        );
      } else {
        dispatch(
          addItemsToGrid({
            id: currentCollectionId ? COLLECTION_MEDIA_GRID_ID : getGridIdForTabStates(activeTab, activeSubtab),
            items: gridMediaItems,
            index: 0,
          })
        );
        dispatch(updateUnlinkedMediaState(gridMediaItems));
      }
    }
    onMediaUploaded(mediaItems, currentUploadIndex, totalUploadsCount);
  };

  const processAfterUpload = (type: UserMediaType, data: UserMediaVOResponse[] | UserMediaVOResponse): void => {
    const mediaItems = [];
    const gridItems: Record<string, GridItemStorage> = {};
    if (Array.isArray(data)) {
      for (const itemData of data) {
        gridItems[itemData.filename] = getMediaStorageForbackendData(itemData, type);
        mediaItems.push(getMediaStorageForbackendData(itemData, type));
      }
    } else {
      gridItems[data.filename] = getMediaStorageForbackendData(data, type);
      mediaItems.push(getMediaStorageForbackendData(data, type));
    }
    if (isUserLoggedIn() && !currentCollectionId) {
      dispatch(
        addOrReplaceGridItems({
          gridId: getGridIdForTabStates(activeTab, activeSubtab),
          gridItems,
        })
      );
    } else {
      dispatch(
        addItemsToGrid({
          id: currentCollectionId ? COLLECTION_MEDIA_GRID_ID : getGridIdForTabStates(activeTab, activeSubtab),
          items: mediaItems,
          index: 0,
        })
      );
      dispatch(updateUnlinkedMediaState(mediaItems));
    }
  };

  const getInputForm = (): ReactElement => {
    return (
      <div className="_hidden">
        <form action={window.PMW.util.site_url()} method="post" encType="multipart/form-data">
          <input type="hidden" name="csrf_token" value="" className="js-csrf-input" aria-hidden="true" />
          <input type="file" className="hidden" name="FileData" multiple accept={getAcceptMimetypes()} aria-label="Upload" ref={fileUploadInputRef} />
        </form>
      </div>
    );
  };
  const getAcceptMimetypes = (): string => {
    if (uploadAcceptedMimetype?.length) {
      return uploadAcceptedMimetype;
    }
    if (!currentCollectionId) {
      return 'image/*,image/heic,image/heif,video/*,image/gif,audio/*';
    }
    switch (activeTab) {
      case UploadsMediaType.IMAGE:
        return 'image/*,image/heic,image/heif';
      case UploadsMediaType.VIDEO:
        return 'video/*,image/gif';
      case UploadsMediaType.AUDIO:
        return 'audio/*';
      default:
        throw new Error(`Invalid media type ${activeTab}`);
    }
  };
  const getUploadMediaButtonText = (): string => {
    if (isForDesignUpload) {
      return window.i18next.t('pmwjs_upload_media'); // this used to be pmwjs_upload_design, but was changed
      // temporarily until a more consistent terminology between 'Upload'/'Upload Media'/'Upload Design' is decided
    }
    if (!currentCollectionId) {
      if (mediaTypesToShow.length > 1) {
        return window.i18next.t('pmwjs_upload_media');
      }
      if (mediaTypesToShow.includes(UploadsMediaType.IMAGE)) {
        return window.i18next.t('pmwjs_btn_upload_photos');
      }
      if (mediaTypesToShow.includes(UploadsMediaType.VIDEO)) {
        return window.i18next.t('pmwjs_btn_upload_videos');
      }
      if (mediaTypesToShow.includes(UploadsMediaType.AUDIO)) {
        return window.i18next.t('pmwjs_btn_upload_audios');
      }
    }
    switch (activeTab) {
      case UploadsMediaType.IMAGE:
        return window.i18next.t('pmwjs_btn_upload_photos');
      case UploadsMediaType.VIDEO:
        return window.i18next.t('pmwjs_btn_upload_videos');
      case UploadsMediaType.AUDIO:
        return window.i18next.t('pmwjs_btn_upload_audios');
      default:
        throw new Error(`Invalid media type ${activeTab}`);
    }
  };

  const onClickRecordOptions = (id: RECORD_DROPDOWN_OPTIONS): void => {
    props.onClickRecordOptions(id);
  };

  const getButtonsRowForUpload = (): ReactElement => {
    return (
      <div className="spacing-m-t-4 flex-row-align-center flex-content-center _full-width">
        <Button
          onClick={() => {
            trackPosterBuilderGA4Events(GA4EventName.UPLOADSMODAL_UPLOAD_MEDIA);
          }}
          key="upload-media"
          type={Type.PRIMARY}
          size={Size.SMALL}
          icon="icon-upload-1"
          customClasses={`spacing-m-l-4 ${styles.uploadBtn} ${styles.childHoverOnly}`}
          text={getUploadMediaButtonText()}
        />
        {!hideRecordingOptions ? (
          <RecordDropdown
            defaultTab={defaultTab}
            panelId={props.panelId}
            customClasses={styles.childHoverOnly}
            mediaTypesToShow={mediaTypesToShow}
            onClose={onClose}
            onClickOption={onClickRecordOptions}
          />
        ) : null}
        {getAiOfferingButton()}
      </div>
    );
  };

  const getAiOfferingButtonForTab = (): ReactElement | null => {
    let callback, key, icon, text;
    switch (activeTab) {
      case UploadsMediaType.IMAGE_BACKGROUND:
      case UploadsMediaType.IMAGE:
        callback = props.onClickAiImage.bind(null);
        key = 'upload-ai-image';
        icon = 'icon-ai-image';
        text = window.i18next.t('pmwjs_ai_image');
        break;
      case UploadsMediaType.AUDIO:
        callback = props.onClickAiVoice.bind(null);
        key = 'upload-ai-voice';
        icon = 'icon-ai-voice';
        text = window.i18next.t('pmwjs_ai_voice');
        break;
      default:
        return null;
    }

    return (
      <Button
        onClick={callback}
        key={key}
        type={Type.SECONDARY}
        size={Size.SMALL}
        icon={icon}
        customClasses={`spacing-m-l-4 ${styles.childHoverOnly}`}
        textClasses={styles.capitalize}
        iconClassName={styles.aiIcon}
        text={text}
      />
    );
  };

  const getAiOfferingButton = (): ReactElement | null => {
    if (currentCollectionId !== '') {
      return getAiOfferingButtonForTab();
    }
    const aiEnabledOptions = mediaTypesToShow.filter((type) => {
      return type;
    });
    if (aiEnabledOptions.length > 1) {
      return (
        <AiOptionsDropdown
          selectorClasses={styles.childHoverOnly}
          mediaTypesToShow={mediaTypesToShow}
          onClick={(listItemId, event) => {
            if (event) {
              switch (listItemId as UserMediaPanelAiOption) {
                case UserMediaPanelAiOption.AI_VOICE:
                  props.onClickAiVoice(event);
                  break;
                case UserMediaPanelAiOption.AI_IMAGE:
                  props.onClickAiImage(event);
                  break;
                default:
                  throw new Error(`Unhandled list item id for AI options: ${listItemId}`);
              }
            }
          }}
        />
      );
    } else if (aiEnabledOptions.length === 1) {
      return getAiOfferingButtonForTab();
    }
    return null;
  };

  const getContent = (): ReactElement => {
    return (
      <ClickableDiv
        className={`flex-center flex-v-row _full-width _full-height radius-4 upload-btn-container hover-transition-all ${styles.uploadButtonContainer}`}
        onClick={onUploadItem}
      >
        <div className={`dragdropoverlay _full-width _full-height radius-4 ${styles.dragdropoverlay}`} />
        <Text bold className={`spacing-p-t-2 content-sub-text ${styles.zIndex}`} val={window.i18next.t('pmwjs_upload_box')} type={TextType.BODY} size={TextSize.SMALL} />
        <Text
          className={`spacing-p-t-0 content-sub-text ${styles.zIndex}`}
          val={hideRecordingOptions ? window.i18next.t('pmwjs_upload_box_subtext_no_record') : window.i18next.t('pmwjs_upload_box_subtext')}
          size={TextSize.XXSMALL}
        />
        {getButtonsRowForUpload()}
      </ClickableDiv>
    );
  };
  const getSmallScreenContent = (): ReactElement => {
    return (
      <Button
        key="upload-media"
        type={Type.PRIMARY}
        customClasses={`${btnClasses} _full-width`}
        size={Size.MEDIUM}
        iconPlacement={IconPlacement.LEFT}
        icon="icon-upload-1"
        text={getUploadMediaButtonText()}
        onClick={() => {
          trackPosterBuilderGA4Events(GA4EventName.UPLOADSMODAL_UPLOAD_MEDIA);
          onUploadItem();
        }}
      />
    );
  };

  return (
    <>
      {getInputForm()}
      {isSmallScreen ? getSmallScreenContent() : getContent()}
    </>
  );
}
